import { useCallback, useRef, ReactElement, useState } from "react";

// Third parties
import { useDispatch, useSelector } from "react-redux";

// Evergiving
import { PaymentFormConfig } from "@evergiving/common/dist/es2015/form";

import {
  PaymentSource,
  PaymentSourceKind,
  SubmissionResult,
} from "@evergiving/common/dist/es2015/pledge";

import { pick } from "@evergiving/common/dist/es2015/typescriptUtils";

// Form components
import { PaymentSection } from "@/lib/components/payment";
import { SubmitButton } from "@/lib/components/SubmitButton";
import {
  SubmitWithStripeButton,
  StripeCheckoutOutcome,
} from "@/lib/components/SubmitWithStripeButton";
import { SubmissionInProgressOverlay } from "@/lib/components/SubmissionInProgressOverlay";

// Hooks
import { isWalletKind, walletConfigFor } from "@/lib/misc/walletKind";
import { useFormRouter } from "@/lib/hooks/useFormRouter";
import { useScrollIntoViewOnAppearance } from "@/lib/hooks/useScrollIntoViewOnAppearance";
import { useFormSubmission } from "@/lib/hooks/useFormSubmission";
import { Submission, SubmissionState } from "@/lib/hooks/useSubmissionState";
import {
  usePaymentCommon,
  StripePaymentContext,
} from "@/lib/hooks/usePaymentCommon";

// Redux
import { initialStateFromFormConfig } from "@/lib/redux/store";
import { RootState } from "@/lib/redux/types";
import { setPartialState } from "@/lib/redux/actions";

import { fetchAblyMessage } from "@/lib/misc/AblyMessage";

export const PledgePayment = ({
  formConfig,
  alwaysVisible,
}: {
  formConfig: PaymentFormConfig;
  alwaysVisible?: boolean;
}): ReactElement => {
  const {
    accountIdentificationFromFields,
    canSubmitCreditCardPayment,
    canSubmitDirectDebitPayment,
    creditCardPaymentFields,
    directDebitPaymentFields,
    paymentSourceKind,
  } = usePaymentCommon({ formConfig });

  const formSubmission = useFormSubmission(formConfig);
  const dispatch = useDispatch();
  const formRouter = useFormRouter();

  const [submission, setSubmissionState] = useState<Submission>({
    state: SubmissionState.Unsubmitted,
  });

  const verificationCode = useSelector(
    (state: RootState) => state.cardVerificationCode
  );

  const handleCloseSubmissionDialog = useCallback(() => {
    setSubmissionState({ state: SubmissionState.Unsubmitted });
  }, []);

  const canSubmit =
    (paymentSourceKind === PaymentSourceKind.CreditCard &&
      canSubmitCreditCardPayment(creditCardPaymentFields)) ||
    (paymentSourceKind === PaymentSourceKind.DirectDebit &&
      canSubmitDirectDebitPayment(directDebitPaymentFields)) ||
    isWalletKind(paymentSourceKind);

  const ref = useRef<HTMLDivElement>(null);

  useScrollIntoViewOnAppearance(ref, !alwaysVisible);

  const handleSubmission = useCallback(
    async (
      stripePaymentContext?: StripePaymentContext
    ): Promise<StripeCheckoutOutcome> => {
      let paymentSource: PaymentSource;
      if (paymentSourceKind === PaymentSourceKind.CreditCard) {
        if (!canSubmitCreditCardPayment(creditCardPaymentFields)) {
          throw new Error("Unexpected state");
        }

        paymentSource = {
          kind: PaymentSourceKind.CreditCard,
          ...pick(
            creditCardPaymentFields,
            "holderName",
            "primaryAccountNumber",
            "expiry"
          ),
          verificationCode,
        };
      } else if (paymentSourceKind === PaymentSourceKind.DirectDebit) {
        const accountIdentification = accountIdentificationFromFields(
          directDebitPaymentFields
        );

        if (
          !accountIdentification ||
          !canSubmitDirectDebitPayment(directDebitPaymentFields)
        ) {
          throw new Error("Unexpected state");
        }

        paymentSource = {
          kind: PaymentSourceKind.DirectDebit,
          accountHolderName:
            directDebitPaymentFields.directDebitAccountHolder.value,
          accountIdentification,
        };
      } else if (isWalletKind(paymentSourceKind)) {
        if (stripePaymentContext == null) {
          throw new Error("Unexpected state");
        }

        // Name the wallet that actually paid, not just "some wallet", so the
        // submission lambda can check it against what the link authorises.
        paymentSource = {
          kind: stripePaymentContext.walletKind,
        };
      } else {
        throw new Error("Not yet implemented");
      }

      setSubmissionState({ state: SubmissionState.Submitting });
      try {
        const response = await formSubmission.submit({
          paymentSource,
          stripePaymentContext,
        });
        const { result } = response;

        switch (result) {
          case "clientError":
          case SubmissionResult.GeneralError:
          case SubmissionResult.StripeError:
            setSubmissionState({
              state: SubmissionState.SubmissionError,
              error: "message" in response ? response.message : undefined,
            });
            return {
              failed: true,
              message: "message" in response ? response.message : undefined,
            };
          case SubmissionResult.StripeRequiresAction:
            if (response.ablyClientKey !== undefined) {
              setSubmissionState({
                state: SubmissionState.RequiresAction,
                actionUrl: response.nextAction.redirectToUrl,
              });

              // Redirect to auth url (3DS) in new window
              const authWindow = window.open(
                response.nextAction.redirectToUrl,
                "_blank",
                "noopener=true, noreferrer=true"
              );

              const ablyMessage = await fetchAblyMessage({
                apiKey: response.ablyClientKey,
                channelName: `df-${response.state.submissionUuid}`,
              });

              if (authWindow) {
                authWindow.close();
              }

              if (ablyMessage.error) {
                setSubmissionState({
                  state: SubmissionState.SubmissionError,
                  error: ablyMessage.message,
                });
                return { failed: true, message: ablyMessage.message };
              }

              dispatch(setPartialState(initialStateFromFormConfig(formConfig)));
              formRouter.replaceWithPage("thanks");
              return { failed: false };
            }

            setSubmissionState({ state: SubmissionState.SubmissionError });
            return { failed: true };
          case "timeoutError":
          case SubmissionResult.Success:
            dispatch(setPartialState(initialStateFromFormConfig(formConfig)));
            formRouter.replaceWithPage("thanks");
            return { failed: false };
          default:
            ((x: never) => {
              throw new Error(`${String(x)} was unhandled!`);
            })(result);
        }
      } catch {
        const message = "Unable to complete payment.";
        setSubmissionState({
          state: SubmissionState.SubmissionError,
          error: message,
        });
        return { failed: true, message };
      }
    },
    [
      paymentSourceKind,
      formSubmission,
      dispatch,
      creditCardPaymentFields,
      verificationCode,
      directDebitPaymentFields,
      canSubmitDirectDebitPayment,
      formConfig,
      formRouter,
    ]
  );

  return (
    <div ref={ref}>
      {formConfig.payment ? (
        <PaymentSection
          config={formConfig.payment}
          autoFocus={!alwaysVisible}
        />
      ) : null}

      {isWalletKind(paymentSourceKind) &&
      walletConfigFor(formConfig.payment, paymentSourceKind) ? (
        <SubmitWithStripeButton
          canSubmit={
            canSubmit && submission.state === SubmissionState.Unsubmitted
          }
          charityName={formConfig.charityNameText}
          onSubmit={handleSubmission}
          config={walletConfigFor(formConfig.payment, paymentSourceKind)!}
          kind={paymentSourceKind}
        />
      ) : (
        <SubmitButton
          canSubmit={
            canSubmit && submission.state === SubmissionState.Unsubmitted
          }
          onSubmit={handleSubmission}
          submitButtonText={formConfig.submitButtonText}
        />
      )}

      <SubmissionInProgressOverlay
        onClose={handleCloseSubmissionDialog}
        paymentFieldConfig={formConfig.payment}
        submission={submission}
      />
    </div>
  );
};
